import { StorageRootDto } from "../StorageRootDto";
import { ValueEncoder, ObjUtils } from "DiscoveryEngine.TS/Utils";
import { UpdateResultDto } from 'UvExplorerServer.Dtos.TS';
import { Privileges } from "./Privileges";
import { UserSettingsDto } from "UvExplorerServer.Dtos.TS/Configuration/UserSettingsDto";


export enum UserClass
{
	Administrator,
	Regular,
	Observer,
}


export enum AccountType {
	Native,
	ActiveDirectory,
	GoogleWorkspace,
	Saml,
}


export function getAccountTypeLabel(accountType: AccountType): string {
	switch (accountType) {
		case AccountType.ActiveDirectory:
			return "Active Directory";
		case AccountType.GoogleWorkspace:
			return "Google Workspace";
		case AccountType.Native:
			return "UVexplorer";
		case AccountType.Saml:
			return "SAML Single Sign-On";
		default:
			return "";
	}
}


export class UserDto extends StorageRootDto {

	private static readonly Default_AccountType = AccountType.Native;
	private static readonly Default_EmailVerified = false;
	private static readonly Default_IsActive = true;
	private static readonly Default_UserClass = UserClass.Regular;

	public AccountType: AccountType;
	public Subject: string;
	public Username: string;
	public FirstName: string;
	public LastName: string;
	public Email: string;
	public EmailVerified: boolean;
	public IsActive: boolean;
	public HashedPassword: string;
	public DirectoryId: string;
	public DirectoryId_2: string;
	public DirectoryDomainName: string;
	public UserClass: UserClass;
	public Privileges: Privileges;
	public UserSettings: UserSettingsDto;

	constructor() {
		super();

		this.AccountType = UserDto.Default_AccountType;
		this.Subject = "";
		this.Username = "";
		this.FirstName = "";
		this.LastName = "";
		this.Email = "";
		this.EmailVerified = UserDto.Default_EmailVerified;
		this.IsActive = UserDto.Default_IsActive;
		this.HashedPassword = "";
		this.DirectoryId = "";
		this.DirectoryId_2 = "";
		this.DirectoryDomainName = "";
		this.UserClass = UserDto.Default_UserClass;
		this.Privileges = new Privileges();
		this.UserSettings = new UserSettingsDto();
	}

	public get IsAdmin(): boolean {
		return (this.UserClass == UserClass.Administrator);
	}

	public get IsRegular(): boolean {
		return (this.UserClass == UserClass.Regular);
	}

	public get IsObserver(): boolean {
		return (this.UserClass == UserClass.Observer);
	}

	public get HasSubject(): boolean {
		return !!this.Subject;
	}

	public get HasUsername(): boolean {
		return !!this.Username;
	}

	public get HasFirstName(): boolean {
		return !!this.FirstName;
	}

	public get HasLastName(): boolean {
		return !!this.LastName;
	}

	public get HasEmail(): boolean {
		return !!this.Email;
	}

	public get HasHashedPassword(): boolean {
		return !!this.HashedPassword;
	}

	public get HasDirectoryId(): boolean {
		return !!this.DirectoryId;
	}

	public get HasDirectoryId_2(): boolean {
		return !!this.DirectoryId_2;
	}

	public get HasDirectoryDomainName(): boolean {
		return !!this.DirectoryDomainName;
	}

	public get HasPrivileges(): boolean {
		return (!!this.Privileges && this.Privileges.HasEntries);
	}

	public CopyProps(obj: UpdateResultDto, isCreate: boolean = true) {
		super.CopyProps(obj, isCreate);

		if (isCreate) {
			this.Subject = obj.Subject;
		}
	}

	public Equals(obj: any): boolean {
		if (!super.Equals(obj))
			return false;

		if (!(obj instanceof UserDto))
			return false;
		let other: UserDto = (obj as UserDto);

		return (ObjUtils.Equals(this.AccountType, other.AccountType) &&
			ObjUtils.Equals(this.Subject, other.Subject) &&
			ObjUtils.Equals(this.Username, other.Username) &&
			ObjUtils.Equals(this.FirstName, other.FirstName) &&
			ObjUtils.Equals(this.LastName, other.LastName) &&
			ObjUtils.Equals(this.Email, other.Email) &&
			ObjUtils.Equals(this.EmailVerified, other.EmailVerified) &&
			ObjUtils.Equals(this.IsActive, other.IsActive) &&
			ObjUtils.Equals(this.HashedPassword, other.HashedPassword) &&
			ObjUtils.Equals(this.DirectoryId, other.DirectoryId) &&
			ObjUtils.Equals(this.DirectoryId_2, other.DirectoryId_2) &&
			ObjUtils.Equals(this.DirectoryDomainName, other.DirectoryDomainName) &&
			ObjUtils.Equals(this.UserClass, other.UserClass) &&
			ObjUtils.Equals(this.Privileges, other.Privileges));
	}

	public Write(obj: any, context: any): void {
		obj.user = {};
		obj = obj.user;
		this.UserSettings.Write(obj, context);
		super.Write(obj, context);

		if (this.AccountType !== UserDto.Default_AccountType)
			obj.account_type = AccountType[this.AccountType];

		if (this.HasSubject)
			obj.subject = this.Subject;

		if (this.HasUsername)
			obj.user_name = this.Username;

		if (this.HasFirstName)
			obj.first_name = this.FirstName;

		if (this.HasLastName)
			obj.last_name = this.LastName;

		if (this.HasEmail)
			obj.email = this.Email;

		if (this.EmailVerified !== UserDto.Default_EmailVerified)
			obj.email_verified = ValueEncoder.EncodeBoolean(this.EmailVerified);

		if (this.IsActive !== UserDto.Default_IsActive)
			obj.is_active = ValueEncoder.EncodeBoolean(this.IsActive);

		if (this.HasHashedPassword)
			obj.hashed_password = this.HashedPassword;

		if (this.HasDirectoryId)
			obj.directory_id = this.DirectoryId;

		if (this.HasDirectoryId_2)
			obj.directory_id_2 = this.DirectoryId_2;

		if (this.HasDirectoryDomainName)
			obj.directory_domain_name = this.DirectoryDomainName;

		if (this.UserClass !== UserDto.Default_UserClass)
			obj.user_class = UserClass[this.UserClass];

		if (this.HasPrivileges)
			this.Privileges.Write(obj, context);
	}

	public Read(obj: any, context: any): void {

		if (!obj.user)
			throw new Error();
		obj = obj.user;

		super.Read(obj, context);

		if (obj.account_type !== undefined)
			this.AccountType = AccountType[<string>obj.account_type];

		if (obj.subject !== undefined)
			this.Subject = obj.subject;

		if (obj.user_name !== undefined)
			this.Username = obj.user_name;

		if (obj.first_name !== undefined)
			this.FirstName = obj.first_name;

		if (obj.last_name !== undefined)
			this.LastName = obj.last_name;

		if (obj.email !== undefined)
			this.Email = obj.email;

		if (obj.email_verified !== undefined)
			this.EmailVerified = ValueEncoder.DecodeBoolean(obj.email_verified);

		if (obj.is_active !== undefined)
			this.IsActive = ValueEncoder.DecodeBoolean(obj.is_active);

		if (obj.hashed_password !== undefined)
			this.HashedPassword = obj.hashed_password;

		if (obj.directory_id !== undefined)
			this.DirectoryId = obj.directory_id;

		if (obj.directory_id_2 !== undefined)
			this.DirectoryId_2 = obj.directory_id_2;

		if (obj.directory_domain_name !== undefined)
			this.DirectoryDomainName = obj.directory_domain_name;

		if (obj.user_class !== undefined)
			this.UserClass = UserClass[<string>obj.user_class];

		if (obj.privileges !== undefined) {
			this.Privileges.Read(obj, context);
		}

		if (obj.user_settings !== undefined) {
			this.UserSettings.Read(obj, context);
		}
	}

}